import type {TabsProps} from 'antd';
import {Skeleton, Tabs} from 'antd';
import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import TabBio from '../Tabs/Profile';
import TabAdminProfile from '../Tabs/AdminMemberProfile';
import TabTransactions from '../Tabs/Transactions';
import TabGifting from '../Tabs/Gifting';
import TabBenefits from '../Tabs/Benefits';
import TabSubscriptions from '../Tabs/Subscription';
import styles from './styles';
import './styles.css';
import TabQueryForm from '../Tabs/QueryForm';
import TabGiftingHistory from '../Tabs/GiftingHistory';
import TabServiceRequests from '../Tabs/ServiceRequests';
import {useData} from 'Context/DataContext';
import MemberDetailsPanel from 'Pages/Tabs/MemberDetailsPanel';
import {useLocation} from 'react-router-dom';
import {TabPanelType} from 'Utils/enums';
import GMGifitngHistory from 'Pages/Tabs/GMGiftingHistory';
import TabGMGifting from 'Pages/Tabs/GMGifting';
import TabEvent from '../Tabs/Events';
import TabTransactionLogs from 'Pages/Tabs/TransactionLogs';

type TabDef = [
  enabled: boolean,
  key: string,
  label: string,
  content: React.ReactNode,
  disabled?: boolean,
];

const Dashboard = () => {
  const location: any = useLocation();
  const state = location?.state;
  const {iframe: isIframe} = useData();
  const [loading, setLoading] = useState(true);
  const {selectedMember} = useSelector((state: any) => state?.selectedMember);
  const roleConfig = useSelector((state: any) => state?.login?.roleConfig);
  var selectedTab = TabPanelType.memberProfile;
  for (let tab in TabPanelType) {
    if (tab in roleConfig) {
      selectedTab = TabPanelType[tab];
      break;
    }
  }
  const [activeTab, setActiveTab] = React.useState(state?.tab || selectedTab);

  /**
   * IFRAME setup part 2
   *
   * in useEffect we check if the request is from iframe, if first load we set the member details in context
   * once the member details are present in context we set loading to false and show the page, if it's a reload then the member is there in session storage
   * and will get picked up by the context on app load - so we just set loading to false
   *
   * IMPORTANT
   * the isIframe flag stored in storage is used to control what components are shown in the iframe and what tabs from the dashboard
   */

  useEffect(() => {
    setLoading(false);
  }, []);

  const handleChange = (key: string) => {
    setActiveTab(key);
  };

  if (loading) {
    return <Skeleton active avatar paragraph={{rows: 4}} />;
  }

  const isTransactionDisabled = selectedMember?.[0]?.status.toLowerCase() === 'inactive';

  const allTabDefs: TabDef[] = [
    [!!roleConfig?.subscription, TabPanelType.subscription, 'Subscription', <TabSubscriptions />],
    [!!roleConfig?.benefits, TabPanelType.benefits, 'Benefits', <TabBenefits />],
    [!!roleConfig?.memberProfile, TabPanelType.memberProfile, 'Profile', <TabBio />],
    [
      !!roleConfig?.adminMemberProfile,
      TabPanelType.adminMemberProfile,
      'Member Profile',
      <TabAdminProfile />,
    ],
    [
      !!roleConfig?.serviceRequests,
      TabPanelType.serviceRequests,
      'Service Requests',
      <TabServiceRequests />,
    ],
    [
      !!roleConfig?.transaction,
      TabPanelType.transaction,
      'Transactions',
      <TabTransactions />,
      isTransactionDisabled,
    ],
    [!!roleConfig?.reissuance, TabPanelType.reissuance, 'Reissuance', <TabGifting />],
    [
      !!roleConfig?.reissuanceHistory,
      TabPanelType.reissuanceHistory,
      'Reissuance History',
      <TabGiftingHistory />,
    ],
    [!!roleConfig?.giftCardCatalog, TabPanelType.giftCardCatalog, 'Gift Cards', <TabGMGifting />],
    [
      !!roleConfig?.newTransaction,
      TabPanelType.transactionLogs,
      'Transactions',
      <TabTransactionLogs />,
    ],
    [!!roleConfig?.event, TabPanelType.events, 'Audit Logs', <TabEvent />],
    // need to render according to role config -- TO DO
    [
      !!roleConfig?.giftCardHistory,
      TabPanelType.giftingHistory,
      'Gifting History',
      <GMGifitngHistory />,
    ],
    [!!roleConfig?.queryForm, TabPanelType.queryForm, 'Query Form', <TabQueryForm />],
  ];

  const iframeTabDefs: TabDef[] = [
    [
      !!roleConfig?.transaction,
      TabPanelType.transaction,
      'Transactions',
      <TabTransactions />,
      isTransactionDisabled,
    ],
    [!!roleConfig?.reissuance, TabPanelType.reissuance, 'Reissuance', <TabGifting />],
    [
      !!roleConfig?.reissuanceHistory,
      TabPanelType.reissuanceHistory,
      'Reissuance History',
      <TabGiftingHistory />,
    ],
  ];

  const items: TabsProps['items'] = (isIframe ? iframeTabDefs : allTabDefs)
    .filter(([enabled]) => enabled)
    .map(([, key, label, content, disabled]) => ({
      key,
      label,
      disabled,
      children: <div style={styles.tabPanelContainer}>{content}</div>,
    }));

  return (
    <div style={styles.container}>
      <div style={{display: 'flex'}}>
        {roleConfig?.adminMemberProfile ? (
          <div style={{position: 'sticky', top: 0}}>
            <MemberDetailsPanel />
          </div>
        ) : null}
        <Tabs
          centered
          activeKey={activeTab}
          onChange={handleChange}
          items={items}
          style={{flex: 1}}
          className="dashboard-tabs"
          // matches @mui/lab's TabPanel, which unmounts inactive panels rather
          // than keeping them mounted-but-hidden — several of these tabs
          // (Transactions, Gifting, forms) rely on refetching on every visit.
          destroyOnHidden
        />
      </div>
    </div>
  );
};

export default Dashboard;
